/**
 * A paleta de avatar do produto, e o único lugar onde ela existe.
 *
 * Havia cinco listas diferentes (`AddToProjectForm`, `TeamAddMemberPanel`,
 * `TeamImportPanel`, `modals/TeamModal`, `ResourceManager`) mais um sexto valor
 * padrão solto no `capacityService`. Duas delas guardavam a string
 * `'var(--color-primary)'` como se fosse cor, e esse valor era **gravado no
 * banco** em `resources.avatar_color` / `team_members.avatar_color`.
 *
 * Isso produzia dois defeitos reais:
 *  - o seletor de cor da edição nunca aparecia marcado para quem foi cadastrado
 *    pelo Portfólio, porque a comparação era contra hexadecimais;
 *  - a cor "primária" gravada não acompanha troca de tema como as outras, já que
 *    virava texto opaco no meio de um `style`.
 *
 * Aqui a paleta é de hexadecimais, de propósito: é cor de DADO (identidade de
 * uma pessoa), não cor de chrome. Cor de chrome sai de `var(--color-*)`.
 */

// Sem `as const`: com tipo literal, qualquer `useState` que derive a cor daqui
// fica preso ao primeiro valor da lista.
export const AVATAR_PALETTE: string[] = [
  '#7e3eee', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444',
  '#ec4899', '#8b5cf6', '#06b6d4', '#f97316', '#64748b',
];

export const DEFAULT_AVATAR_COLOR = AVATAR_PALETTE[0];

/**
 * Cores que o produto gravou no passado e que não estão mais na paleta.
 * Traduzir na leitura evita ter que corrigir o banco antes de a tela ficar
 * certa, e mantém a pessoa com a cor mais próxima da que ela já tinha.
 */
const LEGACY_MAP: Record<string, string> = {
  'var(--color-primary)': '#7e3eee',
  '#e74c3c': '#ef4444', // vermelho antigo do ResourceManager
  '#2ecc71': '#22c55e',
  '#9b59b6': '#8b5cf6',
  '#f39c12': '#f59e0b',
  '#1abc9c': '#06b6d4',
  '#e67e22': '#f97316',
  '#3498db': '#3b82f6',
  '#E57373': '#ef4444', // paleta material do TeamModal
  '#81C784': '#22c55e',
  '#FFB74D': '#f59e0b',
  '#BA68C8': '#8b5cf6',
  '#4FC3F7': '#06b6d4',
  '#FF8A65': '#f97316',
  '#AED581': '#22c55e',
  '#F06292': '#ec4899',
  '#7986CB': '#3b82f6',
};

/**
 * Devolve uma cor da paleta canônica a partir do que estiver gravado.
 * Vazio vira o padrão; valor legado vira o equivalente novo; valor já
 * canônico passa direto. Qualquer outra coisa (hex escolhido à mão em versões
 * antigas) é respeitada: é dado do usuário, não vamos reescrever a cor dele.
 */
export const normalizeAvatarColor = (value?: string | null): string => {
  if (!value) return DEFAULT_AVATAR_COLOR;
  const trimmed = value.trim();
  if (LEGACY_MAP[trimmed]) return LEGACY_MAP[trimmed];
  // `var(...)` nunca deveria ter virado dado: se aparecer outro, cai no padrão.
  if (trimmed.startsWith('var(')) return DEFAULT_AVATAR_COLOR;
  return trimmed;
};

/** A cor está marcada neste swatch? Compara já normalizando os dois lados. */
export const isSameAvatarColor = (a?: string | null, b?: string | null): boolean =>
  normalizeAvatarColor(a).toLowerCase() === normalizeAvatarColor(b).toLowerCase();

/**
 * Cor estável a partir do nome, para quem nunca escolheu uma.
 * Determinística de propósito: a mesma pessoa não pode trocar de cor a cada
 * render nem entre telas.
 */
export const avatarColorFor = (seed: string): string => {
  if (!seed) return DEFAULT_AVATAR_COLOR;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

/**
 * Iniciais do avatar: duas letras quando há sobrenome, uma quando não há.
 * O produto tinha as duas regras convivendo (o Portfólio usava uma letra, o
 * painel da pessoa usava duas), então a mesma pessoa aparecia diferente
 * dependendo da tela.
 */
export const initialsFor = (name?: string | null): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};
